import {ChatPageContext, PageManifest} from './pageContextTypes'
import {SCREEN_LIST_MAX_ITEMS, fitScreenSnapshot} from './screenText'

const PAGE_CONTEXT_ID = /^[a-z0-9_.:-]{1,64}$/
const PAGE_CONTEXT_PATH_MAX_LENGTH = 200

function asRecord(value: unknown): Record<string, unknown> | null {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function asId(value: unknown): string | null {
  return typeof value === 'string' && PAGE_CONTEXT_ID.test(value) ? value : null
}

function idList(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return [...new Set(value.map(asId).filter((id): id is string => id != null))].slice(
    0,
    SCREEN_LIST_MAX_ITEMS
  )
}

/**
 * `options.pageContext` exactly as it may leave the browser, or null when it has no usable
 * surface. Ids must match `^[a-z0-9_.:-]{1,64}$`; `path` is cut at the query string and capped;
 * `dom` goes through the same label rules and size budget the collector applies. Anything else
 * the caller put on the object is dropped.
 */
export function sanitizePageContext(raw: unknown): ChatPageContext | null {
  const record = asRecord(raw)
  if (!record || typeof record.surface !== 'string' || !record.surface.trim()) return null

  const context: ChatPageContext = {surface: record.surface.trim()} as ChatPageContext
  if (typeof record.path === 'string') {
    context.path = record.path.split(/[?#]/)[0].slice(0, PAGE_CONTEXT_PATH_MAX_LENGTH)
  }

  const page = asRecord(record.page)
  const pageId = page ? asId(page.id) : null
  if (page && pageId) {
    const tab = asId(page.tab)
    context.page = {id: pageId, ...(tab ? {tab} : {})}
  }

  const visibleRecord = asRecord(record.visible)
  if (visibleRecord) {
    const visible: NonNullable<PageManifest['visible']> = {}
    ;(['tabs', 'sections', 'actions'] as const).forEach((key) => {
      const ids = idList(visibleRecord[key])
      if (ids.length > 0) visible[key] = ids
    })
    if (Object.keys(visible).length > 0) context.visible = visible
  }

  const dom = fitScreenSnapshot(record.dom)
  if (dom) context.dom = dom

  return context
}
